import { ReactNode, useEffect, useRef, useState } from 'react';
import { PaymentError, ValidationError } from 'request-sender';
import { FormContextData, FormFieldsData } from './FormContext';
import _, { set } from 'lodash';
import useValidator, {
  FieldValidationSchema,
  useRequiredConstraintValidator,
  ValidationErrors,
  ValidationSchema
} from 'validation-schema';
import { ViewStyle } from 'react-native';
import { FormLayoutProps } from './types';
import { useEventsPublisher } from 'event-bus';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
export interface AfterSubmitProps<ExecuteType, RedirectType = string> {
  redirect?: RedirectType;
  message?: string;
  execute?: ExecuteType;
}

function cloneDeep(object: any) {
  return _.cloneDeep(object);
}

export type GetDataFunc<DataType = FormFieldsData> = Nullable<() => Promise<DataType>>;
export type SubmitFormFunc<DataType = FormFieldsData, ResponseType = DataType>
  = (values: DataType) => Promise<ResponseType>;
export type AfterSubmitFunc<DataType = FormFieldsData> = (data: DataType) => Promise<void>;

export interface FormAttributes<DataType = FormFieldsData, ResponseType = DataType> {
  initialData?: Partial<DataType>;
  children: ReactNode
  getDataFunc?: GetDataFunc<DataType>;
  beforeValidationFunc?: (data: DataType) => DataType,
  submitFormFunc: SubmitFormFunc<DataType, ResponseType>,
  afterSubmitFunc?: AfterSubmitFunc<ResponseType>,
  validationSchema?: ValidationSchema | ((data: DataType) => ValidationSchema),
  clearAfterSubmit?: boolean,
  buttons?: ReactNode,
  title?: ReactNode,
  className?: string,
  containerStyle?: ViewStyle,
  formBodyStyle?: ViewStyle,
  buttonsBlockStyle?: ViewStyle,
  buttonsBlockClassName?: string,
  submitOnEachChange?: boolean,
  isDisabled?: boolean | ((data: DataType) => boolean)
  layout?: ReactComponent<FormLayoutProps>,
  testID?: string,
  updateFormDataAfterSubmit?: boolean,
  cancelFunc?: () => Promise<void>,
  dependencies?: any[],
}

export const VALIDATION_ERROR_OCCURRED = 'VALIDATION_ERROR_OCCURRED';

/**
 * Hook includes common logic of form components.
 *
 * @param formAttributes
 */
function useForm<DataType = FormFieldsData, ReponseType = DataType>(
  formAttributes: FormAttributes<DataType, ReponseType>
): FormContextData<DataType> {
  const [_rendered, setRendered] = useState(false);

  const [disabledAutoSubmit, setDisableAutoSubmit] = useState(false);

  const requiredConstraintValidator = useRequiredConstraintValidator();

  const {
    initialData
  } = formAttributes;

  const validator = useValidator();

  const eventsPublisher = useEventsPublisher();

  /**
   * Contains form data.
   */
  const [data, setData] = useState<DataType | null>((initialData || {}) as DataType);

  const [disabled, setDisabled] = useState<boolean>(false);

  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);

  const [errors, setErrors] = useState<ValidationErrors>({});

  const registeredFields = useRef<Set<string>>(new Set());

  const [loading, setLoading] = useState(false);

  const [version, setVersion] = useState<number>(0);

  const shouldSubmitOnEachChange = () => {
    return !!formAttributes.submitOnEachChange;
  };

  const { submitFormFunc } = formAttributes;

  useEffect(() => {
    setData((initialData || {}) as DataType);
  }, formAttributes.dependencies || []);

  const onChangeCallback = (values: FormFieldsData): void => {

    setData((prevData) => {
      const copyPrev = cloneDeep(prevData);

      Object.entries(values)
        .forEach((entry) => {
          const [source, value] = entry;
          set(copyPrev, source, value);
        });

      return copyPrev;
    });
  };

  useEffect(() => {
    setRendered(true);
  }, []);

  useEffect(() => {
    if (typeof formAttributes.isDisabled === 'boolean') {
      setDisabled(formAttributes.isDisabled!);
    }
  }, [formAttributes.isDisabled]);

  const getValidationSchema = () => {
    if (formAttributes.validationSchema) {
      if (typeof formAttributes.validationSchema === 'function') {
        return formAttributes.validationSchema(data!);
      } else {
        return formAttributes.validationSchema;
      }
    } else {
      return null;
    }
  };

  const getSubmittedData = () => {
    let submittedData;
    if (formAttributes.beforeValidationFunc) {
      submittedData = formAttributes.beforeValidationFunc(data!);
    } else {
      submittedData = data!;
    }

    return submittedData;
  }

  const isValid = async (): Promise<boolean> => {
    const submittedData = getSubmittedData();

    const schema = getValidationSchema();
    if (schema) {
      const result = await validator.validate(submittedData, schema);

      setErrors(result);
      if (Object.keys(result).length === 0) {
        return true;
      } else {
        eventsPublisher.publish(VALIDATION_ERROR_OCCURRED, result);
        return false;
      }
    } else {
      return true;
    }
  };

  const clearForm = async () => {
    setData({} as DataType);
  };

  const shouldClearAfterSubmit = () => {
    return formAttributes.clearAfterSubmit || false;
  };

  /**
   * Function triggers form submission.
   */
  const submitForm = async (callAfterSubmit = true) => {
    if (await isValid()) {
      setLoading(true);

      const submittedData = getSubmittedData();

      try {
        const response = await submitFormFunc(submittedData);

        if (callAfterSubmit && formAttributes.afterSubmitFunc !== undefined) {
          await formAttributes.afterSubmitFunc(response);
        }

        if (formAttributes.updateFormDataAfterSubmit) {
          setData(response as DataType);
        }

        if (shouldClearAfterSubmit()) {
          clearForm();
        }

        return true;
      } catch (e) {
        if (e instanceof ValidationError) {
          setErrors(e.errors);
          eventsPublisher.publish(VALIDATION_ERROR_OCCURRED, e.errors);
        } else if (e instanceof PaymentError) {
          setErrors({
            __form: ['Payment error occurred. Please update your payment method.']
          });
        } else {
          setErrors({
            __form: ['Unknown error occurred. Please try again later or contact support']
          });
          // eslint-disable-next-line no-console
          console.error(e);
        }

        return false;
        // eslint-disable-next-line no-empty
      } finally {
        setLoading(false);
      }
    } else {
      return false;
    }

  };

  useEffect(() => {
    if (_rendered && shouldSubmitOnEachChange() && !disabledAutoSubmit) {
      submitForm();
    }

    setDisableAutoSubmit(false);

    if (typeof formAttributes.isDisabled === 'function') {
      setDisabled(formAttributes.isDisabled(data!));
    }
  }, [JSON.stringify(data)]);

  const setFormData = (formData: DataType) => {
    setData(formData);
    setDisableAutoSubmit(true);
    setVersion((version) => version + 1);
  };

  const getFieldErrors = (source: string): string[] => {
    if (source !== '__form') {
      registeredFields.current.add(source);
    }
    return errors[source] || [];
  };

  const getUnmappedErrors = (): string[] => {
    const unmapped: string[] = [];
    Object.entries(errors).forEach(([key, messages]) => {
      if (key !== '__form' && !registeredFields.current.has(key)) {
        messages.forEach((message) => {
          unmapped.push(`${key}: ${message}`);
        });
      }
    });
    return unmapped;
  };

  const isDisabled = () => {
    return disabled;
  };

  const getFieldSchema = (fieldName: string) => {
    const chunks = fieldName.split('.');
    const validationSchema = getValidationSchema();

    if (validationSchema) {
      let fieldSchema: FieldValidationSchema | undefined = validationSchema[chunks[0]];

      let i = 1;
      while (!!fieldSchema && i < chunks.length) {
        if (fieldSchema.properties) {
          fieldSchema = fieldSchema.properties[chunks[i]];
        } else {
          fieldSchema = undefined;
        }
        i += 1;
      }

      return fieldSchema;
    } else {
      return undefined;
    }
  };

  const getConstraintValue = (fieldName: string, constraintName: string) => {
    const fieldSchema: FieldValidationSchema | undefined = getFieldSchema(fieldName);

    if (fieldSchema) {
      if (typeof fieldSchema.constraints[constraintName] === 'object') {
        return fieldSchema.constraints[constraintName].value;
      } else {
        return fieldSchema.constraints[constraintName];
      }
    } else {
      return false;
    }
  };

  const getValidatedObject = (fieldName: string) => {
    const parts = fieldName.split('.');
    // remove last part from parts
    parts.pop();

    let result: any = data || {};

    parts.forEach((part) => {
      result = result[part] || {};
    });

    return result;
  };

  const isFieldRequired = (fieldName: string) => {
    return requiredConstraintValidator.isRequired(
      getValidatedObject(fieldName),
      getFieldSchema(fieldName)?.constraints.required
    );
  };

  const __setErrors = (value: Record<string, string[]>) => {
    setErrors(value);
  };

  const isSubmitDisabled = () => {
    return submitDisabled || isDisabled();
  };

  return {
    data,
    loading,
    onChangeCallback,
    setFormData,
    submitForm,
    getFieldErrors,
    getUnmappedErrors,
    clearForm,
    isDisabled,
    setDisabled,
    getConstraintValue,
    isFieldRequired,
    setSubmitDisabled,
    isSubmitDisabled,
    __setErrors,
    version,
    validate: isValid,
  };
}

export { useForm };
